import { createTheme } from '@mui/material';
import { defaultTypography } from './components/typographyStyles';
import { createButtonStyles } from './components/buttonStyles';
import { createPaperStyles, createCardStyles } from './components/paperStyles';
import { 
  createOutlinedInputStyles, 
  createInputLabelStyles, 
  createFormHelperTextStyles 
} from './components/formStyles';

const baseTheme = createTheme({
  palette: {
    primary: {
      main: '#2F4F68', // Steel blue
      light: '#5A7A92',
      dark: '#1E3447',
    },
    secondary: {
      main: '#D9A566', // Amber/wood tone
      light: '#F5D7A7', // Lighter amber for better contrast
      dark: '#B58344',
    },
    background: {
      default: '#F5F1E8', // Parchment/canvas-like background
      paper: '#FFFBF2',   // Cream for cards and surfaces
    },
    error: {
      main: '#A13E26', // Rusty red
    },
    warning: {
      main: '#BC7C43', // Burnt orange
    },
    success: {
      main: '#4A6B8A', // Steel blue success
    },
    text: {
      primary: '#1a1a1a', // Soft black for comfortable readability
      secondary: '#424242', // Dark gray for secondary text
    },
  },
  typography: defaultTypography,
  shape: {
    borderRadius: 4, // Less rounded, more rugged edges
  },
});

// Create the theme with component overrides
const defaultTheme = createTheme({
  ...baseTheme,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'footer': {
          backgroundColor: '#1E3447 !important', // primary.dark - steel blue
          backgroundImage: 'linear-gradient(#1E3447f2, #1E3447f7) !important',
          color: '#F5D7A7 !important', // Updated secondary.light for better contrast
          borderTop: '2px solid #D9A566 !important', // secondary.main
          position: 'relative !important',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '5px',
            backgroundImage: 'linear-gradient(90deg, transparent 0%, #D9A56666 50%, transparent 100%)',
          },
          '& .MuiTypography-root': {
            color: '#F5D7A7 !important',
          },
          '& a.MuiLink-root': {
            color: '#D9A566 !important',
          },
          '& a.MuiLink-root:hover': {
            color: '#F5D7A7 !important',
          },
        },
      },
    },
    MuiButton: createButtonStyles(baseTheme),
    MuiCard: createCardStyles(baseTheme),
    MuiPaper: createPaperStyles(baseTheme),
    MuiOutlinedInput: createOutlinedInputStyles(baseTheme),
    MuiInputLabel: createInputLabelStyles(baseTheme),
    MuiFormHelperText: createFormHelperTextStyles(baseTheme),
    MuiContainer: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
          backgroundColor: '#1E3447', // primary.dark - steel blue
          borderBottom: '2px solid #D9A566', // secondary.main - amber accent
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E3447', // primary.dark - steel blue
          color: '#F5D7A7', // Updated secondary.light - light amber text for better contrast
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '.MuiAppBar-root &': {
            color: '#F5D7A7', // Updated secondary.light for better contrast
            '&:hover': {
              color: '#D9A566', // secondary.main
            },
            '& .MuiAvatar-root': {
              backgroundColor: '#5A7A92 !important', // Lighter blue background in navbar
              color: '#FFFBF2 !important', // Light text for good contrast
            },
          }
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '.MuiAppBar-root &, .MuiToolbar-root &': {
            color: '#F5D7A7', // Updated secondary.light - light amber text in navbar for better contrast
          },
        },
        // Headers use steel blue for nautical theme
        h1: { color: '#2F4F68' }, // Steel blue for main headers
        h2: { color: '#2F4F68' }, // Steel blue for section headers
        h3: { color: '#2F4F68' }, // Steel blue for subsection headers
        h4: { color: '#2F4F68' }, // Steel blue for smaller headers
        h5: { color: '#1E3447' }, // Darker steel blue for minor headers
        h6: { color: '#1E3447' }, // Darker steel blue for smallest headers
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: '#5A7A92 !important', // primary.light - lighter blue background
          color: '#FFFBF2 !important', // Light text for good contrast on blue background
          '&': {
            backgroundColor: '#5A7A92', // Ensure background is applied
            color: '#FFFBF2',
          },
        },
        colorDefault: {
          backgroundColor: '#5A7A92 !important',
          color: '#FFFBF2 !important',
        },
      },
    },
  },
});

export default defaultTheme; 